import React, { useState } from 'react';
import {
    FileText,
    FileCode,
    FileSpreadsheet,
    FileImage,
    FileArchive,
    FileAudio,
    FileVideo,
    Mail,
    Presentation,
    File,
    AlignJustify,
    FilePlus2,
    BookOpen,
    Layers,
    Contact,
} from 'lucide-react';

interface DocumentThumbnailProps {
    thumbnail_url?: string;
    title: string;
    hasThumbnail?: boolean;
    category?: string;
    fileType?: string;
    size?: 'sm' | 'md' | 'lg';
    onClick?: () => void;
}

const DocumentThumbnail: React.FC<DocumentThumbnailProps> = ({
    thumbnail_url,
    title,
    hasThumbnail = false,
    category,
    fileType,
    size = 'md',
    onClick
}) => {
    const [imageError, setImageError] = useState(false);

    // Determine the size class based on the size prop
    const sizeClass = {
        sm: 'h-12 w-12',
        md: 'h-16 w-16',
        lg: 'h-35 w-25',
    }[size] || 'h-16 w-16';

    // Determine the icon size based on the thumbnail size
    const iconSize = {
        sm: 24,
        md: 32,
        lg: 40
    }[size] || 32;

    // Determine if we should show a placeholder
    const showPlaceholder = !hasThumbnail || !thumbnail_url || imageError;

    // Get a guaranteed icon based on title if nothing else is provided
    const getFallbackIcon = () => {
        if (!title) return <File size={iconSize} />;

        // Use first letter of title to pick a consistent icon
        const firstChar = title.charAt(0).toLowerCase();
        const charCode = firstChar.charCodeAt(0);

        // Use modulo to select from our available icons
        const iconOptions = [
            <FileText size={iconSize} />,
            <FilePlus2 size={iconSize} />,
            <BookOpen size={iconSize} />,
            <Layers size={iconSize} />,
            <Contact size={iconSize} />,
            <AlignJustify size={iconSize} />
        ];

        return iconOptions[charCode % iconOptions.length];
    };

    // Determine which placeholder icon to use based on file type or category
    const getPlaceholderIcon = () => {
        // Default icon in case everything else fails
        let iconElement = <File size={iconSize} />;

        // First check file type
        if (fileType) {
            const lowerFileType = fileType.toLowerCase();
            if (lowerFileType.includes('pdf')) {
                iconElement = <File size={iconSize} />;
            } else if (lowerFileType.includes('doc') || lowerFileType.includes('txt')) {
                iconElement = <FileText size={iconSize} />;
            } else if (lowerFileType.includes('xls') || lowerFileType.includes('csv') || lowerFileType.includes('sheet')) {
                iconElement = <FileSpreadsheet size={iconSize} />;
            } else if (lowerFileType.includes('jpg') || lowerFileType.includes('jpeg') ||
                       lowerFileType.includes('png') || lowerFileType.includes('gif')) {
                iconElement = <FileImage size={iconSize} />;
            } else if (lowerFileType.includes('zip') || lowerFileType.includes('rar')) {
                iconElement = <FileArchive size={iconSize} />;
            } else if (lowerFileType.includes('mp3') || lowerFileType.includes('wav') || lowerFileType.includes('audio')) {
                iconElement = <FileAudio size={iconSize} />;
            } else if (lowerFileType.includes('mp4') || lowerFileType.includes('avi') ||
                       lowerFileType.includes('mov') || lowerFileType.includes('video')) {
                iconElement = <FileVideo size={iconSize} />;
            } else if (lowerFileType.includes('ppt') || lowerFileType.includes('presentation')) {
                iconElement = <Presentation size={iconSize} />;
            } else if (lowerFileType.includes('js') || lowerFileType.includes('py') ||
                       lowerFileType.includes('java') || lowerFileType.includes('html') ||
                       lowerFileType.includes('code')) {
                iconElement = <FileCode size={iconSize} />;
            }
        }

        // Then check category if we're still using the default icon
        if (iconElement.type === File && category) {
            const lowerCategory = category.toLowerCase();
            if (lowerCategory.includes('email') || lowerCategory.includes('communication')) {
                iconElement = <Mail size={iconSize} />;
            } else if (lowerCategory.includes('financial')) {
                iconElement = <FileSpreadsheet size={iconSize} />;
            } else if (lowerCategory.includes('legal')) {
                iconElement = <FileText size={iconSize} />;
            } else if (lowerCategory.includes('report')) {
                iconElement = <File size={iconSize} />;
            } else if (lowerCategory.includes('presentation')) {
                iconElement = <Presentation size={iconSize} />;
            }
        }

        // If we still have the default File icon, use our fallback
        if (iconElement.type === File) {
            iconElement = getFallbackIcon();
        }

        return iconElement;
    };

    // Get background color based on category
    const getBgColor = () => {
        if (!category) return 'bg-gray-100';

        const lowerCategory = category.toLowerCase();

        if (lowerCategory.includes('grantee')) {
            return 'bg-blue-50';
        } else if (lowerCategory.includes('investment')) {
            return 'bg-green-50';
        } else if (lowerCategory.includes('internal')) {
            return 'bg-purple-50';
        } else if (lowerCategory.includes('legal')) {
            return 'bg-amber-50';
        } else if (lowerCategory.includes('financial')) {
            return 'bg-emerald-50';
        } else if (lowerCategory.includes('communication') || lowerCategory.includes('email')) {
            return 'bg-indigo-50';
        }

        return 'bg-gray-100';
    };

    return (
        <div
            className={`flex-shrink-0 ${sizeClass} relative overflow-hidden rounded border border-gray-200`}
            onClick={onClick}
        >
            {!showPlaceholder ? (
                <img
                    src={thumbnail_url}
                    alt={title}
                    className="h-full w-full object-cover"
                    onError={() => setImageError(true)}
                />
            ) : (
                <div className={`flex items-center justify-center h-full w-full ${getBgColor()} text-gray-600`}>
                    {getPlaceholderIcon()}
                </div>
            )}
        </div>
    );
};

export default DocumentThumbnail;